/**
 * Single source of truth for assembling the system prompt that goes to the
 * LLM. Consumed by:
 *  - apps/api `buildSystemPrompt`, which joins the texts and ships them.
 *  - apps/web `PreviewSection` on /admin/prompts, which renders one block per
 *    entry with a localized subtitle.
 *
 * Pure function — no I/O, no globals. Tests live in `prompt-assembly.test.ts`.
 */
export function assemblePromptParts(input) {
    const parts = [];
    const trimmedPrefix = input.prefix.trim();
    if (trimmedPrefix) {
        parts.push({ kind: 'prefix', text: trimmedPrefix });
    }
    const mainContent = input.button?.mainPrompt?.content.trim() ?? '';
    if (mainContent) {
        parts.push({
            kind: 'main',
            promptName: input.button.mainPrompt.name,
            text: mainContent,
        });
    }
    const enabled = new Set(input.enabledCheckboxIds);
    const sortedCheckboxes = [...input.checkboxes].sort((a, b) => a.order - b.order);
    let additionalEmitted = 0;
    for (const checkbox of sortedCheckboxes) {
        if (!enabled.has(checkbox.id))
            continue;
        const text = checkbox.prompt?.content.trim() ?? '';
        if (!text)
            continue;
        parts.push({
            kind: 'additional',
            checkboxId: checkbox.id,
            order: checkbox.order,
            promptName: checkbox.prompt.name,
            text,
        });
        additionalEmitted += 1;
    }
    if (additionalEmitted === 0) {
        const defaultExtraContent = input.button?.defaultExtraPrompt?.content.trim() ?? '';
        if (defaultExtraContent) {
            parts.push({
                kind: 'defaultExtra',
                promptName: input.button.defaultExtraPrompt.name,
                text: defaultExtraContent,
            });
        }
    }
    return parts;
}
export function joinPromptParts(parts) {
    return parts.map((p) => p.text).join('\n\n');
}
export function hasMainPart(parts) {
    return parts.some((p) => p.kind === 'main');
}
